import type { ReactNode } from 'react';
import { PrintReport, type PrintReportDetail } from '@/components/common/PrintReport';
import { EMPTY_VALUE } from '@/constants/app.constants';
import type { UserDetail } from '@/features/users/types/user.types';
import { cn } from '@/lib/utils';
import { PRINT_CELL_CLASS, PRINT_TABLE_CLASS } from '@/styles/printTable';
import { formatDisplayDate } from '@/utils/date';
import { formatNumber } from '@/utils/number';
import type { SpecimenDistributionRow } from '../types/product.types';
import { formatDistributedPercent, getInHandQty } from '../utils/distribution';

const REPORT_TITLE = 'Specimen Adjustment Report';
const JOB_LOCATION_SEPARATOR = ', ';

interface AdjustmentPrintColumn {
  id: string;
  header: string;
  isQuantity: boolean;
  render: (row: SpecimenDistributionRow, index: number) => ReactNode;
}

const PRINT_COLUMNS: readonly AdjustmentPrintColumn[] = [
  { id: 'sl', header: 'SL', isQuantity: false, render: (_row, index) => index + 1 },
  { id: 'name', header: 'Specimen Name', isQuantity: false, render: (row) => row.name },
  { id: 'spCode', header: 'Sp Code', isQuantity: false, render: (row) => row.spCode },
  {
    id: 'edition',
    header: 'Edition',
    isQuantity: false,
    render: (row) => row.editionName ?? EMPTY_VALUE,
  },
  {
    id: 'challan',
    header: 'Challan Qty',
    isQuantity: true,
    render: (row) => formatNumber(row.challanQty),
  },
  {
    id: 'adjusted',
    header: 'Adj Qty',
    isQuantity: true,
    render: (row) => formatNumber(row.distributedQty),
  },
  {
    id: 'hand',
    header: 'Hand Qty',
    isQuantity: true,
    render: (row) => formatNumber(getInHandQty(row.challanQty, row.distributedQty)),
  },
  {
    id: 'adjustedPercent',
    header: 'Adj(%)',
    isQuantity: true,
    render: (row) => formatDistributedPercent(row.distributedQty, row.challanQty),
  },
];

const TOTAL_LABEL_COLUMN_COUNT = PRINT_COLUMNS.filter((column) => !column.isQuantity).length;

const getCellClass = (isQuantity: boolean) =>
  cn(PRINT_CELL_CLASS, isQuantity ? 'text-center tabular-nums' : 'text-left');

interface AdjustmentTotals {
  challanQty: number;
  distributedQty: number;
}

interface SpecimenAdjustmentPrintReportProps {
  rows: SpecimenDistributionRow[];
  totals: AdjustmentTotals | undefined;
  dateFrom: string;
  dateTo: string;
  zm: UserDetail | undefined;
}

function getJobLocation(zm: UserDetail | undefined): string | undefined {
  const places = [zm?.thanaName, zm?.districtName].filter(Boolean);
  return places.length > 0 ? places.join(JOB_LOCATION_SEPARATOR) : undefined;
}

export function SpecimenAdjustmentPrintReport({
  rows,
  totals,
  dateFrom,
  dateTo,
  zm,
}: SpecimenAdjustmentPrintReportProps) {
  const details: PrintReportDetail[] = [
    { label: 'Name', value: zm?.fullName },
    { label: 'HR ID', value: zm?.hrEmpId },
    { label: 'Mobile', value: zm?.mobile },
    { label: 'Job Location', value: getJobLocation(zm) },
    { label: 'From Date', value: formatDisplayDate(dateFrom) },
    { label: 'To Date', value: formatDisplayDate(dateTo) },
  ];

  const challanTotal = totals?.challanQty ?? 0;
  const distributedTotal = totals?.distributedQty ?? 0;

  return (
    <PrintReport title={REPORT_TITLE} details={details}>
      <table className={PRINT_TABLE_CLASS}>
        <thead>
          <tr>
            {PRINT_COLUMNS.map((column) => (
              <th key={column.id} className={cn(getCellClass(column.isQuantity), 'font-bold')}>
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={row.specimenId}>
              {PRINT_COLUMNS.map((column) => (
                <td key={column.id} className={getCellClass(column.isQuantity)}>
                  {column.render(row, index)}
                </td>
              ))}
            </tr>
          ))}
          <tr className="font-bold">
            <td colSpan={TOTAL_LABEL_COLUMN_COUNT} className={cn(PRINT_CELL_CLASS, 'text-center')}>
              Grand Total
            </td>
            <td className={getCellClass(true)}>{formatNumber(challanTotal)}</td>
            <td className={getCellClass(true)}>{formatNumber(distributedTotal)}</td>
            <td className={getCellClass(true)}>
              {formatNumber(getInHandQty(challanTotal, distributedTotal))}
            </td>
            <td className={getCellClass(true)}>
              {formatDistributedPercent(distributedTotal, challanTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    </PrintReport>
  );
}
